/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path pages/kpisapproval
 * @description KPI value approval
 *
 * @section 4.1.9.2
 *
 */
/* eslint-disable no-template-curly-in-string, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import IconButton from "@material-ui/core/IconButton";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import SearchIcon from "@material-ui/icons/Search";
import * as registry from "@qpr/client-api-registry";
import { t } from "i18next";
import { sortBy } from "lodash";
import React, { useState } from "react";
import { Column } from "react-table";
import useWorkflowQuery from "./../../apihooks/useMyWorkflows";
import { useAppContext } from "./../../context/AppCtxProvider";
import { mea_t, stringTemplate } from "./../../tplutils/utils";
import SelectorMenu from "./../../ui/components/Selector";
import ShowRejectMessage from "./../../ui/components/ShowRejectMessage";
import WorkflowTable from "./../../ui/components/WorkflowTable";
import { makeThemedStyles } from "./../../viewcomponents/utils";
import { IKpiValues, wfStatesMap } from "./KpisApproval";
type IWorkflowKpiList = {
  query: string;
  criteria: string;
  values: any[];
  attributes?: string;
  isAcceptComponent?: boolean;
  cssPrefix?: string;
  url?: string;
  workflowPeriod?: string;
  workflowSeries?: string;
  workflowType?: string;
  cssVariables?: Array<{
    name: string;
    value: string;
  }>;
  kpiVal?: IKpiValues;
  enabled?: boolean;
};
type IProjectState = {
  id: string;
  name: string;
  JS_ATTR?: string;
  actual?: string;
  target?: string;
  status?: string;
  wfInstanceId?: string;
  period: string;
  workflow?: any;
};
const useStyles = makeThemedStyles("MilestoneInput.Heading", (theme) => ({
  "@global": {
    ".WorkflowKpiList": {
      "& .statusInfo": {
        color: "rgba(227, 181, 7, 1)",
        padding: "2px 14px",
        lineHeight: "20px",
        background: "rgba(227, 181, 7, 0.1)",
        borderRadius: "26px",
      },
      "& .data_entry": {
        color: "rgba(115, 120, 124, 1)",
        background: "rgba(159, 175, 178, 0.1)",
      },
    },
  },
  projectsIcon: {
    width: "2.33em",
    height: "2.16em",
    color: "rgb(0, 169, 163)",
    verticalAlign: "middle",
    margin: "0.3em",
  },
  backButton: {
    width: "2.31em",
    height: "2.25em",
    backgroundColor: "#fff",
    padding: "0.25em",
    margin: "0.25em",
    marginRight: "0.25em",
    color: "rgba(76, 81, 87, 1)",
  },
  pageTitle: {
    margin: "1em",
    fontSize: "2.6em",
    color: "rgb(76,81,87)",
    fontWeight: 500,
    width: "100%",
    display: "flex",
    justifyContent: "start",
    alignItems: "center",
  },
  pageTitleContainer: {
    alignItems: "center",
    paddingRight: "0em",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "-4em",
  },
  helperContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginRight: "3.1em",
  },
  searchOptions: {
    width: "21.73em",
    height: "5.58em",
    borderRadius: "2.85em",
    backgroundColor: "#fff",
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginRight: "0.62em",
    marginLeft: "0.62em",
  },
  inputSearch: {
    display: "block",
    margin: "auto",
    height: "3.14em",
    width: "100%",
    borderRadius: "2.10em",
    border: "none",
    outline: "none",
    fontFamily: "Dubai, Ample",
  },
  tobarIcon: {
    color: "rgba(17, 94, 103, 1)",
    marginLeft: `0.36em`,
    marginRight: "0.33em",
    borderRadius: "50%",
  },
  formControl: {
    margin: theme.spacing(1),
    maxWidth: "94px",
    "& .MuiInputBase-root-224": {},
  },
  filterOptions: {
    width: "100%",
    textAlign: "center",
  },
  AllOptions: {
    width: "90%",
    padding: "0.25em 0.5em",
    fontSize: "1.5em",
    cursor: "pointer",
    lineHeight: "1.1",
    border: "none",
    outline: "none",
    WebkitAppearance: "none",
    color: "grey",
  },
  targetValue: {
    color: "rgba(0, 169, 163, 1)",
    fontSize: "20px",
    lineHeight: "31px",
  },
  actualValue: {
    color: "rgba(17, 94, 103, 1)",
    fontSize: "20px",
    lineHeight: "31px",
  },
  valueSuffix: {
    color: "rgba(76, 81, 87, 1)",
    fontSize: "14px",
    lineHeight: "28px",
  },
  checkIcon: {
    fontSize: "1.1em",
    color: "rgba(0, 169, 163, 1)",
  },
}));
const T = (str: string) => `${t(str)}`;
const WorkflowKpiList = (props: {
  componentData: IWorkflowKpiList;
  options?: any;
  setSelectedRowIs: (value: any) => void;
}) => {
  const classes = useStyles();
  const ctx = useAppContext();
  const [status, setStatus] = React.useState<undefined | string>("");
  const [filteredData, setFilteredData] = React.useState<any[] | undefined>([]);
  const [reload, setReload] = useState(false);
  const [isModalOpen, setModalOpen] = React.useState(false);
  const [rejectmsgm, setRejectmsg] = React.useState("");
  const { setSelectedRowIs } = props;
  const { data, isError } = useWorkflowQuery<IProjectState[]>(
    props.componentData.query,
    {
      attributes: stringTemplate(props.componentData.attributes || "", { ctx }),
      values: props.componentData.values,
      criteria: props.componentData.criteria,
      enabled: props.componentData.enabled,
    }
  );
  const [queryData, setQueryData] = React.useState<any[]>([]);
  const Message = registry.getSvg("Message");
  React.useEffect(() => {
    const val = data?.data || [];
    setQueryData(val);
  }, [data]);
  React.useEffect(() => {
    setFilteredData(queryData);
  }, [queryData]);
  React.useEffect(() => {
    const getSelctedData = queryData?.find(
      (val) =>
        val.id === ctx.elementId &&
        (val?.workflow?.wfInstanceId === ctx?.wfInstanceId ||
          props.componentData?.workflowPeriod === val?.workflow?.period)
    );
    if (!ctx?.wfInstanceId && getSelctedData) {
      ctx.sendValue("wfInstanceId", getSelctedData?.workflow?.wfInstanceId);
    }
    setSelectedRowIs([getSelctedData]);
  }, [queryData.length, ctx]);
  const handleShowMsg = (dataRow: any) => {
    setModalOpen(!isModalOpen);
    setRejectmsg(dataRow.workflow?.latestLog?.message);
  };
  React.useEffect(() => {
    setQueryData((val) => {
      const subdata = props.componentData.kpiVal?.wf?.nextTransitions;
      if (!subdata?.length && props.componentData.kpiVal) {
        const totalData = val.filter(
          (tdata) => tdata.id !== props.componentData.kpiVal?.elementId
        );
        if (!totalData) return val;
        return totalData;
      }
      if (props.componentData.kpiVal && queryData) {
        const updatedValue = props.componentData.kpiVal;
        return val.map((item) => {
          const workflow = updatedValue.wf;
          if (
            item.id === updatedValue.elementId &&
            item?.workflow?.wfInstanceId === workflow?.wfInstanceId
          ) {
            const { actual } = updatedValue;
            return { ...item, actual, workflow };
          }
          return item;
        });
      }
      return val;
    });
    setReload(() => {
      if (
        props.componentData.kpiVal?.wf?.nextTransitions &&
        !props.componentData.kpiVal?.wf?.nextTransitions?.length
      )
        return true;
      return false;
    });
  }, [props.componentData]);
  const columns: Column<IProjectState>[] = React.useMemo(() => {
    let cols: Column<IProjectState>[] = [
      {
        Header: T("KPI Name"),
        accessor: "name",
        Cell: (row: any) => {
          const dataRow = row.row.original as IProjectState;
          return (
            <div>
              <p>
                {mea_t("Name", dataRow.workflow?.translations, ctx.language)}
              </p>
            </div>
          );
        },
      },
    ];
    cols.push({
      Header: T("Status"),
      accessor: "status",
      Cell: (row: any) => {
        const dataRow = row.row.original as IProjectState;
        return (
          <>
            <div
              className={`statusInfo ${(
                dataRow.workflow?.currentState || "nostate"
              )
                .toLowerCase()
                .replace(/\s/g, "_")}`}
            >
              <div>
                <p>
                  {dataRow.workflow?.message ? t(dataRow.workflow.message) : ""}
                </p>
              </div>
            </div>
            {dataRow.workflow?.latestLog?.message &&
              dataRow.workflow?.latestLog?.message.length > 0 && (
                <div className="msgIcon">
                  <Message onClick={() => handleShowMsg(dataRow)} />
                </div>
              )}
          </>
        );
      },
    });
    cols = [
      ...cols,
      {
        Header: T("Period"),
        accessor: "period",
        Cell: (row: any) => {
          const dataRow = row.row.original as IProjectState;
          if (!dataRow.workflow?.period) {
            return <div className="novalue" />;
          }
          return (
            <div className="periodVal">
              <div>
                <p>{dataRow.workflow?.period}</p>
              </div>
            </div>
          );
        },
      },
      {
        Header: T("Target"),
        accessor: "target",
        Cell: (row: any) => {
          if (!row.row.original.target) {
            return <div className="novalue" />;
          }
          return (
            <div className="value">
              <div className="targetValue">
                {row?.row?.original?.target
                  ? T(parseFloat(row.row.original.target).toFixed(2))
                  : ""}
              </div>
              <div className="valueSuffix">
                {row?.row?.original?.unit ? T(row.row.original.unit) : ""}
              </div>
            </div>
          );
        },
      },
      {
        Header: T("Actual"),
        accessor: "actual",
        Cell: (row: any) => {
          if (!row.row.original.actual) {
            return <div className="novalue" />;
          }
          return (
            <div className="value">
              <div className="actualValue">
                {row?.row?.original?.actual
                  ? T(parseFloat(row.row.original.actual).toFixed(2))
                  : ""}
              </div>
              <div className="valueSuffix">
                {row?.row?.original?.unit ? T(row.row.original.unit) : ""}
              </div>
            </div>
          );
        },
      },
    ];
    cols.unshift({
      accessor: "id",
      Header: "",
      Cell: (row: any) => {
        const dataRow = row.row.original as IProjectState;
        const isOn = row.row.original.workflow.done;
        return (
          <div>
            {isOn ? (
              <CheckCircleIcon
                style={{ fontSize: "1.1em", color: "rgba(0, 169, 163, 1)" }}
              />
            ) : (
              ""
            )}
          </div>
        );
      },
    });
    return cols;
  }, [props.componentData.isAcceptComponent, ctx.language]);
  if (isError) {
    return <div>Error loading data</div>;
  }
  if (!data) {
    return null;
  }
  const findSearchData = (list: any[] | undefined, searchdata?: string) => {
    let res: any[] = [];
    if (list && searchdata) {
      list.forEach((elem) => {
        let dataname = elem.name.toLowerCase();
        if (dataname.includes(searchdata.toLowerCase())) {
          res.push(elem);
        }
      });
    } else {
      res = list || [];
    }
    return res;
  };
  const findMatchingData = (list: any[] | undefined, match?: string) => {
    let res: any[] = [];
    if (list && match) {
      list.forEach((elem) => {
        if (elem.workflow?.message === match) {
          res.push(elem);
        }
      });
    } else {
      res = list || [];
    }
    res = sortBy(res, ["name"]).map((e) => {
      if (e.workflow && e.workflow.currentState)
        e.workflow.currentState =
          wfStatesMap.find(
            (el) => el.statename === e.workflow?.currentState.toLowerCase()
          )?.uiname || e.workflow.currentState;
      return e;
    });
    return res;
  };
  const handleSearch = (value: any) => {
    const searchedData = findSearchData(queryData, value);
    setFilteredData(searchedData);
  };
  const PyramidChartIcon = registry.getSvgIcon("AllKPIGraphIcon");
  return (
    <div>
      <div className={classes.pageTitleContainer}>
        <div className={classes.pageTitle}>
          <IconButton className={classes.backButton}>
            {ctx.rtl ? (
              <ChevronRight fontSize="large" />
            ) : (
              <ChevronLeft fontSize="large" />
            )}
          </IconButton>
          <PyramidChartIcon className={classes.projectsIcon} />
          <p>{t("KPI")}</p>
        </div>
        <div className={classes.helperContainer}>
          <div className={classes.searchOptions}>
            <SearchIcon
              fontSize="large"
              className={classes.tobarIcon}
              aria-hidden="true"
            />
            <input
              type="text"
              className={classes.inputSearch}
              placeholder={t("Search for")}
              id="search"
              onChange={(e) => handleSearch(e.target.value)}
              name="search"
            />
          </div>
          <SelectorMenu
            data={[t("Waiting for input"), t("Waiting for approval")]}
            handleOnClick={(selectedStatus: any) => {
              setStatus(selectedStatus);
            }}
            label={t(status || "Filter by status")}
            selectedValue=""
          />
        </div>
      </div>
      <WorkflowTable
        data={findMatchingData(filteredData, status)}
        columns={columns}
        reloadData={reload}
        url={props.componentData.url}
        classPrefix="WorkflowKpi"
      />
      <ShowRejectMessage
        show={isModalOpen}
        message={rejectmsgm}
        handleModel={setModalOpen}
      />
    </div>
  );
};
export default WorkflowKpiList;
